import * as Sentry from '@sentry/react';
import { queryClient } from 'api';
import { faksApiInstance } from 'api/api';
import type { ResellerWording } from 'types/models';
import { tracker } from 'utils/tracking/tracker';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type SignOutResponse = {
  success: boolean;
  single_log_out_url?: string;
};

export type AuthState = {
  challengeToken: string | null;
  phoneNumber: string | null;
  userToken: string | null;
  trueUserId: number | null;
  currentUserId: number | null;
  currentRoleId: number | null;
  onboardingResellerType: ResellerWording;
};

type AuthActions = {
  setChallengeToken: (challengeToken: string) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  setUserToken: (userToken: string) => void;
  setTrueUserId: (trueUserId: number) => void;
  setCurrentUserId: (currentUserId: number) => void;
  setCurrentRoleId: (currentRoleId: number | null) => void;
  setOnboardingResellerType: (onboardingResellerType: ResellerWording | null) => void;
  startImpersonatingUser: (role: { user_id: number; id: number }) => void;
  clearImpersonatedUser: () => void;
  signOut: (options?: {
    userInitiated?: boolean;
    redirectTo?: string;
    signInToken?: string;
  }) => Promise<void>;
  reset: () => Promise<void>;
};

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  challengeToken: null,
  phoneNumber: null,
  userToken: null,
  trueUserId: null,
  currentUserId: null,
  currentRoleId: null,
  onboardingResellerType: 'pharmacy',
};

// Custom storage that migrates from Redux persist format to Zustand format
const migratingStorage = () => ({
  getItem: (name: string): string | null => {
    const zustandValue = localStorage.getItem(name);
    if (zustandValue) {
      return zustandValue;
    }

    // Try to migrate from old Redux persist format
    const reduxRoot = localStorage.getItem('redux-root');

    if (reduxRoot) {
      try {
        const { auth, ...rest } = JSON.parse(reduxRoot);

        if (auth) {
          const authState = JSON.parse(auth);

          // Clean up old auth data from redux-root while preserving navigation
          if (Object.keys(rest).length > 0) {
            localStorage.setItem('redux-root', JSON.stringify(rest));
          } else {
            localStorage.removeItem('redux-root');
          }

          // Return in Zustand persist format
          return JSON.stringify({ state: authState, version: 0 });
        }
      } catch {
        // Silently fail migration, will use default state
      }
    }

    return null;
  },
  setItem: (name: string, value: string): void => {
    localStorage.setItem(name, value);
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
});

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setChallengeToken: (challengeToken: string) => {
        set({ challengeToken });
      },

      setPhoneNumber: (phoneNumber: string) => {
        set({ phoneNumber });
      },

      setUserToken: (userToken: string) => {
        set({ userToken });
      },

      setTrueUserId: (trueUserId: number) => {
        set({ trueUserId });
      },

      setCurrentUserId: (currentUserId: number) => {
        set({ currentUserId });
      },

      setCurrentRoleId: (currentRoleId: number | null) => {
        const currentState = get();

        if (currentRoleId !== currentState.currentRoleId) {
          set({ currentRoleId });
          queryClient.clear();
        }
      },

      setOnboardingResellerType: (onboardingResellerType: ResellerWording | null) => {
        set({ onboardingResellerType: onboardingResellerType || 'pharmacy' });
      },

      startImpersonatingUser: (role: { user_id: number; id: number }) => {
        set({
          currentUserId: role.user_id,
          currentRoleId: role.id,
        });
        queryClient.clear();
      },

      clearImpersonatedUser: () => {
        const { trueUserId } = get();
        set({
          currentUserId: trueUserId,
          currentRoleId: null,
        });
        queryClient.clear();
      },

      signOut: async (options) => {
        const { userToken, onboardingResellerType } = get();
        let res = null;

        if (userToken && options?.userInitiated) {
          res = await faksApiInstance.post<SignOutResponse>(
            'sign_out',
            {},
            { headers: { Authorization: userToken } }
          );
        }

        queryClient.clear();
        Sentry.setUser(null);
        Sentry.setTags({ true_user_id: null });
        tracker.reset();
        window.Intercom?.('shutdown');
        window.Intercom?.('boot', { app_id: import.meta.env.REACT_APP_INTERCOM_APP_ID });

        // Reset state to initial values, but keep onboardingResellerType
        set({ ...initialState, onboardingResellerType });

        // Wait for localstorage to be updated (this operation is defered)
        await new Promise((resolve) => setTimeout(resolve, 0));

        if (res?.single_log_out_url) {
          return window.location.replace(res.single_log_out_url);
        }

        if (options?.redirectTo) {
          return window.location.replace(options.redirectTo);
        }

        if (!options?.signInToken) {
          return window.location.replace('/');
        }
      },

      reset: async () => {
        // Reset state to initial values, but keep onboardingResellerType
        set({ ...initialState, onboardingResellerType: get().onboardingResellerType });

        // Wait for update to be recorded in localStorage
        await new Promise((resolve) => setTimeout(resolve, 0));
      },
    }),
    {
      name: 'auth',
      storage: createJSONStorage(migratingStorage),
    }
  )
);
